import { useEffect, useRef, useState } from "react";
import { Html5Qrcode } from "html5-qrcode";
import { Camera, X, Keyboard } from "lucide-react";

/**
 * Camera-based barcode scanner wrapping html5-qrcode.
 * Props:
 *   onDetected(code: string): called when a barcode is detected.
 *   onClose(): optional close handler.
 *   title: header text (default "Escanear código").
 */
export const BarcodeScanner = ({ onDetected, onClose, title = "Escanear código" }) => {
  const regionId = "elnav-scanner-region";
  const scannerRef = useRef(null);
  const [starting, setStarting] = useState(true);
  const [error, setError] = useState("");
  const [manual, setManual] = useState("");
  const [showManual, setShowManual] = useState(false);
  const detectedRef = useRef(false);

  useEffect(() => {
    let cancelled = false;
    const start = async () => {
      try {
        setStarting(true);
        setError("");
        const html5 = new Html5Qrcode(regionId, { verbose: false });
        scannerRef.current = html5;
        const cameras = await Html5Qrcode.getCameras();
        if (!cameras || cameras.length === 0) {
          throw new Error("No se encontró cámara. Usa entrada manual.");
        }
        const camId = cameras.find((c) => /back|rear|environment/i.test(c.label))?.id || cameras[cameras.length - 1].id;
        if (cancelled) return;
        await html5.start(
          camId,
          { fps: 10, qrbox: { width: 280, height: 180 } },
          (decoded) => {
            if (detectedRef.current) return;
            detectedRef.current = true;
            try { html5.pause(true); } catch { /* noop */ }
            onDetected(decoded);
          },
          () => { /* decode errors are per-frame; ignore */ }
        );
        setStarting(false);
      } catch (e) {
        setError(e.message || "No se pudo iniciar la cámara");
        setStarting(false);
        setShowManual(true);
      }
    };
    start();
    return () => {
      cancelled = true;
      const s = scannerRef.current;
      if (!s) return;
      try {
        const state = typeof s.getState === "function" ? s.getState() : null;
        // Html5QrcodeScannerState.SCANNING = 2, PAUSED = 3
        if (state === 2 || state === 3) {
          s.stop().catch(() => {}).then(() => {
            try { s.clear(); } catch { /* noop */ }
          });
        } else {
          try { s.clear(); } catch { /* noop */ }
        }
      } catch { /* noop */ }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const submitManual = (e) => {
    e.preventDefault();
    const v = manual.trim();
    if (v) onDetected(v);
  };

  return (
    <div
      className="fixed inset-0 z-[200] bg-black/90 backdrop-blur-sm flex items-center justify-center p-4"
      data-testid="barcode-scanner-modal"
    >
      <div className="w-full max-w-lg bg-[#1F1A17] border border-[#C89B3C]/30 rounded-2xl overflow-hidden">
        <div className="flex items-center justify-between p-4 border-b border-white/10">
          <div className="flex items-center gap-2 text-[#F5F0E6]">
            <Camera size={20} className="text-[#C89B3C]" />
            <h3 className="text-lg font-semibold" style={{ fontFamily: "'Playfair Display'" }}>
              {title}
            </h3>
          </div>
          {onClose && (
            <button
              onClick={onClose}
              data-testid="scanner-close-btn"
              className="p-2 hover:bg-white/10 rounded-full text-[#F5F0E6]/80"
              aria-label="Cerrar escáner"
            >
              <X size={20} />
            </button>
          )}
        </div>

        <div className="p-4">
          <div
            id={regionId}
            className="w-full aspect-video bg-black rounded-xl overflow-hidden"
          />
          {starting && (
            <p className="mt-3 text-sm text-[#F5F0E6]/70 text-center">
              Pide permiso de cámara… apunta al código UPC/EAN del producto.
            </p>
          )}
          {error && (
            <p className="mt-3 text-sm text-red-400 text-center" data-testid="scanner-error">
              {error}
            </p>
          )}

          <div className="mt-4">
            <button
              type="button"
              onClick={() => setShowManual((s) => !s)}
              className="w-full flex items-center justify-center gap-2 text-sm text-[#C89B3C] hover:text-[#E6BF5F] py-2"
              data-testid="scanner-toggle-manual"
            >
              <Keyboard size={16} />
              {showManual ? "Ocultar entrada manual" : "¿No lo escanea? Entrada manual"}
            </button>
            {showManual && (
              <form onSubmit={submitManual} className="flex gap-2 mt-2">
                <input
                  value={manual}
                  onChange={(e) => setManual(e.target.value)}
                  placeholder="Código de barras / UPC / EAN"
                  className="flex-1 bg-[#0A0908] border border-white/15 rounded-lg px-3 py-2 text-[#F5F0E6] placeholder-white/40 focus:outline-none focus:border-[#C89B3C]"
                  data-testid="scanner-manual-input"
                  autoFocus
                />
                <button
                  type="submit"
                  className="bg-[#C89B3C] text-[#0A0908] font-semibold px-4 py-2 rounded-lg hover:bg-[#E6BF5F]"
                  data-testid="scanner-manual-submit"
                >
                  Buscar
                </button>
              </form>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};
